// KPIs mode (`/c/{slug}/kpis`; KPIS-MODE, founder-approved 2026-10-22: Chart and Cadence became
// one mode). `/kpis` opens the chart (a client viewer, who has no chart, the scorecard); the
// chart is c.$slug.kpis.chart.tsx; Scorecard (`/kpis/scorecard`, the EOS grid: seats grouped by
// team × the last 13 weeks, months or quarters, with in-place entry, corrections and history),
// a DE's scorecard (`/kpis/de/{id}`, now a redirect to the DE page's KPIs tab), and (MEMBER-ENTRY) My scorecard and My numbers
// (`/kpis/my-scorecard`, `/kpis/my-numbers`) are here and in c.$slug.kpis.mine.tsx; a plain
// member's Scorecard is the grid of the seats whose KPIs it may see (its own, a supervisor's
// branch: `GET org-chart/kpi-visibility`), never the company roll-up. The scorecard pages are the `cadence` gate's (company#reader, `kpi`);
// data entry (`/kpis/entry/hours|numbers`, `/kpis/import`) is managers', and hours and CSV import
// are `core`. Priorities, Issues, Meetings and Vision are their own routes
// (c.$slug.kpis.*.tsx). The open tab and filters of each screen are in the URL beside the focus.
// Every old `/chart/…` and `/cadence/…` URL redirects here (c.$slug.legacy.tsx). FOUNDER-OS
// phase 1: in Founder OS the Scorecard is the roll-up across the companies (`?company=` opens a
// row); `?board=own` is the portfolio's own seats on the ordinary grid.
import { createRoute, Navigate, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { canOpen, GATES, openOnly } from "../extensions/modes";
import { CompanyView, type CompanyViewTab } from "../features/company/company-view";
import {
  patchScorecardSearch,
  type ScorecardSearch,
  validateScorecardSearch,
} from "../features/company/scorecard-search";
import { PortfolioScorecardScreen } from "../features/portfolio/portfolio-scorecard";
import { useCompany, useSession } from "../features/use-feature";
import { keepFocus, validateFocusSearch } from "../focus/focus";
import { ModeGate } from "../shell/mode-gate";
import { companyRoute } from "./c.$slug";

const UUID = /^[0-9a-f-]{36}$/i;

export const kpisRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "kpis",
  component: Outlet,
});

/** `/kpis`: the chart for whoever opens it (every member), else the scorecard. */
export const kpisIndexRoute = createRoute({
  getParentRoute: () => kpisRoute,
  path: "/",
  validateSearch: (search: Record<string, unknown>) => validateFocusSearch(search),
  component: function KpisIndex() {
    const { me } = useSession();
    const company = useCompany();
    const chart = canOpen("chart", me, company);
    return (
      <Navigate
        to={chart ? "/c/$slug/kpis/chart" : "/c/$slug/kpis/scorecard"}
        params={{ slug: company.slug }}
        search={keepFocus as never}
        replace
      />
    );
  },
});

export const scorecardRoute = createRoute({
  getParentRoute: () => kpisRoute,
  path: "scorecard",
  validateSearch: (
    search: Record<string, unknown>,
  ): ReturnType<typeof validateFocusSearch> &
    ScorecardSearch & {
      view?: "reports" | "published";
      board?: "own";
      company?: string;
    } => ({
    ...validateFocusSearch(search),
    ...validateScorecardSearch(search),
    ...(search.view === "reports" || search.view === "published"
      ? { view: search.view as "reports" | "published" }
      : {}),
    // FOUNDER-OS phase 1: the portfolio's own seats (`board=own`) and the company row to open.
    ...(search.board === "own" ? { board: "own" as const } : {}),
    ...(typeof search.company === "string" && UUID.test(search.company)
      ? { company: search.company }
      : {}),
  }),
  component: function ScorecardRoute() {
    const { api } = scorecardRoute.useRouteContext();
    const { view, branch, mine, off, kpi, cols, board, company: row } = scorecardRoute.useSearch();
    const company = useCompany();
    const navigate = useNavigate();
    // FOUNDER-OS phase 1: in Founder OS the scorecard is the roll-up across the companies.
    if (company.kind === "portfolio" && board !== "own")
      return (
        <ModeGate modeKey="cadence" open>
          <PortfolioScorecardScreen api={api} company={company} focusCompany={row} />
        </ModeGate>
      );
    // KPIS-MODE: a plain member (no company roll-up) gets the grid scoped to the seats whose
    // KPIs it may see (its own; a supervisor's branch), one seat scorecard each.
    const cadence = GATES.find((g) => g.key === "cadence");
    const scoped = !!cadence && openOnly(cadence, company);
    const filters: ScorecardSearch = {
      ...(branch ? { branch } : {}),
      ...(mine ? { mine } : {}),
      ...(off ? { off } : {}),
      ...(kpi ? { kpi } : {}),
      ...(cols ? { cols } : {}),
    };
    return (
      <ModeGate modeKey="cadence" open>
        <CompanyView
          api={api}
          company={company}
          scoped={scoped}
          view={view ?? "grid"}
          onView={(next: CompanyViewTab) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const { view: _drop, ...rest } = prev;
                return next === "grid" ? rest : { ...rest, view: next };
              },
            } as never)
          }
          filters={filters}
          onFilters={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => patchScorecardSearch(prev, next),
              replace: true,
            } as never)
          }
        />
      </ModeGate>
    );
  },
});

/**
 * UX pass 1.2: a DE has one page. Its scorecard (was this route's own screen) is the KPIs tab of
 * `/agents/de/{id}`; the old URL redirects there at the Scorecard section, the query kept.
 */
export const kpisDeRoute = createRoute({
  getParentRoute: () => kpisRoute,
  path: "de/$id",
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: "/c/$slug/agents/de/$id",
      params,
      search: { ...(search as Record<string, unknown>), tab: "scorecard" },
      replace: true,
    } as never);
  },
});
